import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { remarkCollapseHorizontalRules } from "../markdown/remarkCollapseHorizontalRules";
import { useNavigate } from "react-router";
import { useTranslation } from "../../localization/useTranslation";
import { useUrlProvider } from "../../useUrlProvider";
import { AiChatMessage, AiChatSourceReference, AttachmentPreviewTarget } from "../AiChat.types";
import { DocumentEntityType } from "../../common/components/search/SearchResultList.types";
import { useAiChatStyles } from "../AiChat.styles";
import { processChildren } from "../citations/aiChatCitationParser";
import { KnownEntityRef, ParsedEntityLink, processEntityLinks } from "../entityLinks/aiChatEntityLinkParser";
import AiChatCitationChip from "../citations/AiChatCitationChip";
import AiChatAttachmentPill from "../attachments/AiChatAttachmentPill";
import { useAiChatDocumentViewer } from "../documentViewer/AiChatDocumentViewerContext";
import { toolCallIcon } from "./toolCallIcon";
import MessageBubbleFeedback from "./MessageBubbleFeedback";

interface MessageBubbleProps {
    messageIndex: number;
    message: AiChatMessage;
    isStreaming: boolean;
    styles: ReturnType<typeof useAiChatStyles>;
    attachmentLookup: Map<string, AttachmentPreviewTarget>;
}

function MessageBubble({ messageIndex, message, isStreaming, styles, attachmentLookup }: MessageBubbleProps) {
    const isUser = message.role === "user";
    const sources = message.sources ?? [];
    const activity = message.activity ?? [];
    const hasActivity = activity.length > 0;
    const hasContent = message.content !== "";
    const isLiveActivity = isStreaming && !hasContent;
    const [userOverride, setUserOverride] = useState<boolean | null>(null);
    const isActivityOpen = userOverride ?? (isLiveActivity || !hasContent);
    const navigate = useNavigate();
    const urlProvider = useUrlProvider();
    const { translateString } = useTranslation();
    const { openAttachmentPreview, closeForEntityLinkNavigation } = useAiChatDocumentViewer();

    function createChip(source: AiChatSourceReference, index: number) {
        const citationKey = `${messageIndex}-${index}`;
        return <AiChatCitationChip key={`citation-${index}`} source={source} index={index} citationKey={citationKey} />;
    }

    function getEntityUrlForType(entityType: string, entityId: string): string | null {
        switch (entityType) {
            case "Contract":
                return urlProvider.getContractOverviewUrl(entityId);
            case "System":
                return urlProvider.getSystemOverviewUrl(entityId);
            case "ProcessingActivity":
                return urlProvider.getProcessingActivityOverviewUrl(entityId);
            case "IncidentLog":
                return urlProvider.getIncidentLogOverviewUrl(entityId);
            default:
                return null;
        }
    }

    function renderEntityLink(link: ParsedEntityLink, key: number): React.ReactNode {
        if (link.entityType === "ConversationAttachment") {
            return (
                <a
                    key={`entity-${messageIndex}-${key}`}
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        openAttachmentPreview({ id: link.entityId, fileName: link.displayText });
                    }}
                >
                    {link.displayText}
                </a>
            );
        }
        const url = getEntityUrlForType(link.entityType, link.entityId);
        if (!url) return link.displayText;
        return (
            <a
                key={`entity-${messageIndex}-${key}`}
                href={url}
                onClick={(e) => {
                    e.preventDefault();
                    closeForEntityLinkNavigation();
                    navigate(url);
                }}
            >
                {link.displayText}
            </a>
        );
    }

    // Pool the canonical entity ids the backend emitted for this turn — both standalone entity
    // references (e.g. tool results) and the citation sources — so the parser can repair tokens
    // whose GUIDs the LLM mangled when rendering them. Sources carry the entity type as a numeric
    // DocumentEntityType enum, so we project it back to its string name to match the token's
    // entityType segment ("Contract", "System", …).
    const knownEntities: KnownEntityRef[] = [
        ...(message.entityReferences ?? []).map((e) => ({ entityType: e.entityType, entityId: e.entityId })),
        ...sources.map((s) => ({ entityType: DocumentEntityType[s.entityType] as string, entityId: s.entityId })),
    ];

    function withCitations(elementProps: { children?: React.ReactNode }) {
        let children = processEntityLinks(elementProps.children, renderEntityLink, knownEntities);
        if (sources.length > 0) children = processChildren(children, sources, createChip);
        return children;
    }

    const markdownComponents = {
        table: ({ children }: { children?: React.ReactNode }) => (
            <div className="table-wrapper">
                <table>{children}</table>
            </div>
        ),
        a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
            <a href={href} target="_blank" rel="noopener noreferrer">
                {children}
            </a>
        ),
        p: ({ children }: { children?: React.ReactNode }) => <p>{withCitations({ children })}</p>,
        li: ({ children }: { children?: React.ReactNode }) => <li>{withCitations({ children })}</li>,
        td: ({ children }: { children?: React.ReactNode }) => <td>{withCitations({ children })}</td>,
        th: ({ children }: { children?: React.ReactNode }) => <th>{withCitations({ children })}</th>,
        h1: ({ children }: { children?: React.ReactNode }) => <h1>{withCitations({ children })}</h1>,
        h2: ({ children }: { children?: React.ReactNode }) => <h2>{withCitations({ children })}</h2>,
        h3: ({ children }: { children?: React.ReactNode }) => <h3>{withCitations({ children })}</h3>,
        h4: ({ children }: { children?: React.ReactNode }) => <h4>{withCitations({ children })}</h4>,
        h5: ({ children }: { children?: React.ReactNode }) => <h5>{withCitations({ children })}</h5>,
        h6: ({ children }: { children?: React.ReactNode }) => <h6>{withCitations({ children })}</h6>,
    };

    if (isUser) {
        const attachments = message.attachments ?? [];
        return (
            <>
                {message.content !== "" && (
                    <Box data-message-index={messageIndex} sx={styles.userMessage}>
                        {message.content}
                    </Box>
                )}
                {attachments.length > 0 && (
                    <Box data-message-index={message.content === "" ? messageIndex : undefined} sx={styles.userAttachmentBubble}>
                        {attachments.map((a) => (
                            <AiChatAttachmentPill key={a.id} attachment={a} onPreview={openAttachmentPreview} />
                        ))}
                    </Box>
                )}
            </>
        );
    }

    return (
        <Box data-message-index={messageIndex} sx={styles.assistantMessageContainer}>
            {hasActivity && (
                <Box sx={styles.activityPanel}>
                    <Box sx={styles.activityHeader} component="button" onClick={() => setUserOverride(!isActivityOpen)}>
                        <Typography sx={styles.activityHeaderText}>
                            {message.usedDeepAnalysis
                                ? isLiveActivity
                                    ? translateString("aiChatDeepAnalysis")
                                    : translateString("aiChatShowDeepAnalysis")
                                : isLiveActivity
                                  ? translateString("aiChatThinking")
                                  : translateString("aiChatShowThinking")}
                        </Typography>
                        {isActivityOpen ? <ExpandLessIcon sx={styles.activityToggleIcon} /> : <ExpandMoreIcon sx={styles.activityToggleIcon} />}
                    </Box>
                    {isActivityOpen && (
                        <Box sx={styles.activityBody}>
                            {activity.map((entry, i) => {
                                const isLastEntry = i === activity.length - 1;
                                if (entry.kind === "tool_call") {
                                    const isActiveToolCall = isLiveActivity && isLastEntry;
                                    const resolvedAttachment = entry.attachmentId ? attachmentLookup.get(entry.attachmentId) : undefined;
                                    const stripTrailing = (s: string) => s.replace(/[.…]+\s*$/u, "");
                                    let label: string;
                                    if (resolvedAttachment) label = `${stripTrailing(entry.text)}: `;
                                    else if (isActiveToolCall) label = entry.text;
                                    else label = stripTrailing(entry.text);
                                    return (
                                        <Box key={i} sx={styles.activityRow}>
                                            <Box sx={styles.activityRowIcon}>{toolCallIcon(entry.toolName)}</Box>
                                            <Typography component="span" sx={isActiveToolCall ? styles.activityToolCallActive : styles.activityToolCall}>
                                                {label}
                                            </Typography>
                                            {resolvedAttachment && (
                                                <Box
                                                    component="button"
                                                    type="button"
                                                    onClick={() => openAttachmentPreview(resolvedAttachment)}
                                                    sx={styles.activityToolCallLink}
                                                >
                                                    {resolvedAttachment.fileName}
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                }
                                const isActivelyStreaming = isLiveActivity && isLastEntry;
                                return (
                                    <Box key={i} sx={styles.activityRow}>
                                        <Box sx={styles.activityRowDot} />
                                        <Box sx={styles.activityThinking}>
                                            {isActivelyStreaming ? (
                                                <Box sx={styles.activityThinkingPlain}>{entry.text}</Box>
                                            ) : (
                                                <ReactMarkdown remarkPlugins={[remarkGfm, remarkCollapseHorizontalRules]} components={markdownComponents}>
                                                    {entry.text}
                                                </ReactMarkdown>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            )}
            {hasContent && (
                <Box sx={styles.assistantMessage}>
                    <ReactMarkdown remarkPlugins={[remarkGfm, remarkCollapseHorizontalRules]} components={markdownComponents}>
                        {message.content}
                    </ReactMarkdown>
                    {message.resultsTruncated && <Box sx={styles.resultsTruncatedNotice}>{translateString("aiChatResultsTruncatedNotice")}</Box>}
                </Box>
            )}
            {hasContent && !isStreaming && message.id && (
                <MessageBubbleFeedback key={message.id} messageId={message.id} initialIsPositive={message.feedbackIsPositive} styles={styles} />
            )}
        </Box>
    );
}

export default MessageBubble;
